let _firmMap: Record<string, any> = {};
let _firmsData: any[] = [];
let _detectedDomain: string | null = null;

export const setFirmMap = (data: any) => {
  _firmMap = data;
};

export const setFirmsData = (data: any) => {
  _firmsData = data;
};

/**
 * Applies a branding change to the in-memory firms data right after a
 * successful mutation — the counterpart of the react-query cache patches in
 * useUpdateCustomizeTheme's onSuccess. Branding resolved from this copy
 * (e.g. the sidebar logo when impersonating an advisor/HoH) would otherwise
 * keep the page-load snapshot until a full reload.
 */
export const patchFirmBranding = (
  firmId: string | number,
  changes: Record<string, any>,
) => {
  _firmsData = _firmsData.map((firm: any) =>
    firm?.id?.toString() === firmId.toString() ? { ...firm, ...changes } : firm,
  );
  Object.keys(_firmMap).forEach((domain) => {
    if (_firmMap[domain]?.id?.toString() === firmId.toString()) {
      _firmMap[domain] = { ..._firmMap[domain], ...changes };
    }
  });
};

export const setDetectedDomain = (domain: string | null) => {
  _detectedDomain = domain;
};

export const getDetectedDomain = (): string | null => {
  return _detectedDomain;
};

/**
 * Get a firm config value by domain and key
 * @param domain - The domain of the firm without trailing slash
 * @param key - The key of the config value
 * @param fallback - The fallback value if the key is not found
 * @returns The config value or the fallback value
 */
export const getFirmConfig = (
  domain: string,
  key: string,
  fallback?: string,
) => {
  // Use detected domain from server if available (more reliable than window.location.hostname)
  const lookupDomain = _detectedDomain || domain;

  // Try multiple lookup strategies
  const lookupKeys = [
    // Try with detected domain first
    lookupDomain.includes("localhost")
      ? `http://${lookupDomain.split(":")[0]}/`
      : `https://${lookupDomain.split(":")[0]}/`,
    // Fallback to provided domain
    domain.includes("localhost")
      ? `http://${domain.split(":")[0]}/`
      : `https://${domain.split(":")[0]}/`,
    // Try without protocol
    `https://${lookupDomain}/`,
    `http://${lookupDomain}/`,
  ];

  for (const lookupKey of lookupKeys) {
    const value = _firmMap?.[lookupKey]?.[key];
    if (value) {
      return value;
    }
  }

  return fallback;
};

export const getFirmData = (firmId: string) => {
  return _firmsData.find((x: any) => x.id.toString() === firmId);
};

export const getFirmsData = (): any[] => {
  return _firmsData;
};
